if (!customElements.get("quick-add")) {
  // Global cache for /pages/fitsme fetch (shared across all quick-add instances)
  let fitsmeFetchPromise = null;

  function fetchFitsmeProfiles() {
    if (!fitsmeFetchPromise) {
      fitsmeFetchPromise = fetch(`/pages/fitsme?t=${Date.now()}`)
        .then((res) => res.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, "text/html");
          const wrapper = doc.querySelector("#fitsme-select-wrapper");
          if (!wrapper) return null;
          return wrapper;
        })
        .catch((err) => {
          console.error("Failed to fetch fitsme profiles:", err);
          fitsmeFetchPromise = null; // Allow retry on error
          return null;
        });
    }
    return fitsmeFetchPromise;
  }

  function getStorefrontReturnTo() {
    if (window.FoxTheme?.utils?.getStorefrontReturnTo) {
      return window.FoxTheme.utils.getStorefrontReturnTo();
    }

    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
  }

  function buildStorefrontLoginUrl() {
    if (window.FoxTheme?.utils?.buildStorefrontLoginUrl) {
      return window.FoxTheme.utils.buildStorefrontLoginUrl();
    }

    const loginUrl = new URL('/customer_authentication/login', window.location.origin);
    loginUrl.searchParams.set('return_to', getStorefrontReturnTo());
    return `${loginUrl.pathname}${loginUrl.search}`;
  }

  customElements.define(
    "quick-add",
    class QuickAdd extends HTMLElement {
      connectedCallback() {
        this.add_button = this.querySelector("button");
        this.isOverlayOpen = false;

        if (this.add_button) {
          this.add_button.addEventListener("click", (e) => {
            e.preventDefault();
            this.product_url = this.add_button.getAttribute("data-product-url");
            this.fetchProduct(this.product_url);
          });
        }

        this.handleInteractionOutside = this.handleInteractionOutside.bind(this);
      }

      fetchProduct(product_url) {
        if (this.add_button) this.add_button.classList.add("loading");
        const localLoader = this.add_button?.querySelector(".loading-overlay__spinner");
        if (localLoader) localLoader.classList.remove("hidden");

        const section_url = product_url.includes("?")
          ? `${product_url}&section_id=quick-add-view`
          : `${product_url}?section_id=quick-add-view`;

        fetch(section_url)
          .then((res) => res.text())
          .then((res) => {
            if (this.add_button) this.add_button.classList.remove("loading");
            if (localLoader) localLoader.classList.add("hidden");

            const htmlDocument = new DOMParser().parseFromString(res, "text/html");
            const newContent = htmlDocument.querySelector(".quick-add-view");

            if (!newContent) {
              console.error("Could not find .quick-add-view in response");
              return;
            }

            if (this.hasAttribute("data-is-complementary")) {
              newContent.classList.add("is-complementary");
              const options = newContent.querySelectorAll('.wt-product__option');
              options.forEach(opt => {
                const name = opt.getAttribute('data-option-name');
                if (name && name.toLowerCase() !== 'size') {
                  opt.style.display = 'none';
                  const firstRadio = opt.querySelector('input[type="radio"]:not(.disabled)') || opt.querySelector('input[type="radio"]');
                  if (firstRadio) {
                    firstRadio.setAttribute("checked", "checked");
                    firstRadio.checked = true;
                  }
                }
              });
            }

            const form = newContent.querySelector("form");
            const variantOptions = newContent.querySelector("variant-options");

            // Remove existing overlay if any
            this.closeOverlay();

            // Single-variant products do not need an intermediate overlay.
            if (!variantOptions && form) {
              this.removeProfileInputs(form);
              this.submitWithLoading(form);
              return;
            }

            this.isMobile = window.matchMedia("(max-width: 749px)").matches;

            if (this.isMobile) {
              // Bottom sheet: append to body with backdrop
              this.backdrop = document.createElement("div");
              this.backdrop.className = "quick-add-view__backdrop";
              this.backdrop.addEventListener("click", () => this.closeOverlay());
              document.body.appendChild(this.backdrop);
              document.body.appendChild(newContent);
            } else {
              this.appendChild(newContent);
            }
            this.isOverlayOpen = true;

            // Parse custom fit variant map
            const mapScript = newContent.querySelector(".quick-add-view__custom-fit-map");
            this.customFitMap = mapScript ? JSON.parse(mapScript.textContent) : null;

            const closeBtn = newContent.querySelector(".quick-add-view__close");
            if (closeBtn) {
              closeBtn.addEventListener("click", (e) => {
                e.stopPropagation();
                this.closeOverlay();
              });
            }

            // Back button for profile step
            const backBtn = newContent.querySelector(".quick-add-view__back");
            if (backBtn) {
              backBtn.addEventListener("click", (e) => {
                e.stopPropagation();
                this.showVariantStep();
              });
            }

            // Auto-submit on variant selection
            if (variantOptions) {
              const variantDataScript = variantOptions.querySelector('script[type="application/json"]');
              const variants = variantDataScript ? JSON.parse(variantDataScript.textContent) : [];

              variantOptions.addEventListener('change', () => {
                const selectedOptions = Array.from(variantOptions.querySelectorAll('input[type="radio"]:checked')).map(input => input.value);
                
                // Find matching variant
                const currentVariant = variants.find(variant => {
                  return selectedOptions.every((option, index) => {
                    return variant.options[index] === option;
                  });
                });

                if (currentVariant) {
                  const variantId = currentVariant.id.toString();
                  const input = form.querySelector('input[name="id"]');
                  
                  if (input && variantId) {
                    input.value = variantId;

                    // Check if this is a Custom Fit variant
                    if (this.isCustomFitVariant(variantId)) {
                      this.showProfileStep(form, variantId);
                      return;
                    }

                    // Normal variant: submit directly
                    this.removeProfileInputs(form);
                    const clickedLabel = newContent.querySelector(
                      `input[type="radio"]:checked + .quick-add-view__label`,
                    ) || newContent.querySelector('.quick-add-view__label');
                    this.submitWithLoading(form, clickedLabel);
                  }
                }
              });
            }

            document.addEventListener("click", this.handleInteractionOutside);
          })
          .catch((error) => {
            if (this.add_button) this.add_button.classList.remove("loading");
            if (localLoader) localLoader.classList.add("hidden");
            console.error("Fetch error:", error);
          });
      }

      getOverlay() {
        return this.querySelector(".quick-add-view") || document.querySelector("body > .quick-add-view");
      }

      isCustomFitVariant(variantId) {
        if (!this.customFitMap) return false;
        const sizeValue = this.customFitMap[variantId];
        return sizeValue === "Custom Fit" || sizeValue === "Customize";
      }

      showVariantStep() {
        const overlay = this.getOverlay();
        if (!overlay) return;
        const variantStep = overlay.querySelector(".quick-add-view__step-variant");
        const profileStep = overlay.querySelector(".quick-add-view__step-profile");
        const title = overlay.querySelector(".quick-add-view__title");
        const header = overlay.querySelector(".quick-add-view__header");
        if (variantStep) variantStep.style.display = "";
        if (profileStep) profileStep.style.display = "none";
        if (header) header.style.display = "";
        if (title) title.textContent = title.dataset.originalTitle || "SELECT SIZE";
      }

      showProfileStep(form, variantId) {
        const overlay = this.getOverlay();
        if (!overlay) return;
        const variantStep = overlay.querySelector(".quick-add-view__step-variant");
        const profileStep = overlay.querySelector(".quick-add-view__step-profile");
        const profileList = overlay.querySelector(".quick-add-view__profile-list");
        const title = overlay.querySelector(".quick-add-view__title");
        const header = overlay.querySelector(".quick-add-view__header");

        if (!variantStep || !profileStep || !profileList) return;

        // Save original title
        if (title && !title.dataset.originalTitle) {
          title.dataset.originalTitle = title.textContent;
        }
        if (title) title.textContent = "SELECT PROFILE";

        // Switch steps
        variantStep.style.display = "none";
        profileStep.style.display = "";
        if (header) header.style.display = "none";

        // Show loading
        profileList.innerHTML = '<div class="quick-add-view__profile-loading">Loading...</div>';

        fetchFitsmeProfiles().then((wrapper) => {
          if (!wrapper) {
            profileList.innerHTML = '<div class="quick-add-view__profile-message">Could not load profiles.</div>';
            return;
          }

          const loginRequired = wrapper.querySelector("[data-fitsme-login-required]");
          const noProfiles = wrapper.querySelector("[data-fitsme-no-profiles]");
          const select = wrapper.querySelector("[data-fitsme-select]");

          if (loginRequired) {
            this.renderLoginRequired(profileList);
          } else if (noProfiles) {
            this.renderNoProfiles(profileList);
          } else if (select) {
            this.renderProfileOptions(profileList, select, form);
          } else {
            profileList.innerHTML = '<div class="quick-add-view__profile-message">Profile selection unavailable.</div>';
          }
        });
      }

      renderLoginRequired(container) {
        const loginUrl = buildStorefrontLoginUrl();

        container.innerHTML = `
          <div class="quick-add-view__profile-message">
            <p>Sign in to select your measurement profile for Custom Fit.</p>
            <a href="${loginUrl}" class="quick-add-view__profile-action-btn">Sign In</a>
          </div>
        `;
      }

      renderNoProfiles(container) {
        const overlay = this.getOverlay();
        const profileStep = overlay?.querySelector(".quick-add-view__step-profile");
        const baseUrl = profileStep?.dataset.customerMeasurementsUrl || "/account";
        const url = new URL(baseUrl, window.location.origin);
        url.searchParams.set("return_to", getStorefrontReturnTo());

        container.innerHTML = `
          <div class="quick-add-view__profile-message">
            <p>No measurement profiles found. Please create one first.</p>
            <a href="${url}" class="quick-add-view__profile-action-btn">Create Profile</a>
          </div>
        `;
      }

      renderProfileOptions(container, selectEl, form) {
        const options = selectEl.querySelectorAll("option");
        let html = '<ul class="quick-add-view__list">';

        options.forEach((opt, i) => {
          // Skip the empty "No profile selected" and "Add new profile" options
          if (!opt.value || opt.value === "add_new_profile") return;

          const profileData = opt.getAttribute("data-profile") || "";
          const label = opt.textContent.trim();
          const inputId = `quick-add-profile-${i}`;

          html += `
            <li class="quick-add-view__item">
              <input
                type="radio"
                id="${inputId}"
                name="quick-add-profile"
                value="${opt.value}"
                data-profile="${profileData.replace(/"/g, '&quot;')}"
                data-profile-name="${label.replace(/"/g, '&quot;')}"
              >
              <label for="${inputId}" class="quick-add-view__label">
                <span class="quick-add-view__value">${label}</span>
              </label>
            </li>
          `;
        });

        // "Add new profile" link as the last item
        html += `
          <li class="quick-add-view__item">
            <a href="#" class="quick-add-view__label quick-add-view__add-profile-link" data-add-new-profile>
              <span class="quick-add-view__value">+ Add new profile</span>
            </a>
          </li>
        `;
        html += "</ul>";

        container.innerHTML = html;

        // Handle "Add new profile" click
        const addNewLink = container.querySelector("[data-add-new-profile]");
        if (addNewLink) {
          addNewLink.addEventListener("click", (e) => {
            e.preventDefault();
            const overlay = this.getOverlay();
            const profileStep = overlay?.querySelector(".quick-add-view__step-profile");
            const baseUrl = profileStep?.dataset.customerMeasurementsUrl;
            if (baseUrl) {
              const url = new URL(baseUrl);
              url.searchParams.set("return_to", getStorefrontReturnTo());
              window.location.href = url.toString();
            } else {
              window.location.href = "/account";
            }
          });
        }

        // Handle profile radio selection
        container.querySelectorAll('input[name="quick-add-profile"]').forEach((radio) => {
          radio.addEventListener("change", () => {
            const profilePayload = radio.getAttribute("data-profile");
            const profileName = radio.getAttribute("data-profile-name");

            // Inject hidden inputs into the form
            this.setProfileInput(form, "properties[_fitsme_profile]", profilePayload);
            this.setProfileInput(form, "properties[Selected measurement profile]", profileName);

            // Submit with loading on the clicked label
            const clickedLabel = radio.nextElementSibling;
            this.submitWithLoading(form, clickedLabel);
          });
        });
      }

      setProfileInput(form, name, value) {
        let input = form.querySelector(`input[name="${name}"]`);
        if (!input) {
          input = document.createElement("input");
          input.type = "hidden";
          input.name = name;
          form.appendChild(input);
        }
        input.value = value;
      }

      submitWithLoading(form, label) {
        // Show loading spinner on the clicked label
        if (label) {
          label.classList.add("loading");
          const spinner = document.createElement("div");
          spinner.className = "loading-overlay__spinner";
          spinner.innerHTML = '<div class="lds-ring" aria-hidden="true"><div></div><div></div><div></div><div></div></div>';
          label.appendChild(spinner);
        }

        // Also show loading on the quick-add button
        if (this.add_button) this.add_button.classList.add("loading");
        const localLoader = this.add_button?.querySelector(".loading-overlay__spinner");
        if (localLoader) localLoader.classList.remove("hidden");

        const cleanup = () => {
          if (this.add_button) this.add_button.classList.remove("loading");
          if (localLoader) localLoader.classList.add("hidden");
          this.closeOverlay();
        };

        // Submit directly to cart API
        const formData = new FormData(form);

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );
        formData.append('sections', sectionsToBundle);
        formData.append('sections_url', window.location.pathname);

        const config = FoxTheme.utils.fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        fetch(FoxTheme.routes.cart_add_url, config)
          .then((res) => res.json())
          .then(async (parsedState) => {
            if (parsedState.status) {
              console.error('Cart error:', parsedState.description);
              cleanup();
              return;
            }

            const cartJson = await (
              await fetch(FoxTheme.routes.cart_url, { ...FoxTheme.utils.fetchConfig('json', 'GET') })
            ).json();
            cartJson['sections'] = parsedState['sections'];

            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
            document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: parsedState } }));

            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) cartDrawer.show();

            cleanup();
          })
          .catch((err) => {
            console.error('Add to cart error:', err);
            cleanup();
          });
      }

      removeProfileInputs(form) {
        form.querySelector('input[name="properties[_fitsme_profile]"]')?.remove();
        form.querySelector('input[name="properties[Selected measurement profile]"]')?.remove();
      }

      handleInteractionOutside(event) {
        if (!this.isOverlayOpen) return;
        if (this.isMobile) {
          // Mobile: backdrop handles close, ignore outside clicks
          return;
        }
        if (!this.contains(event.target)) {
          this.closeOverlay();
        }
      }

      closeOverlay() {
        this.querySelector(".quick-add-view")?.remove();
        // Mobile bottom sheet: overlay and backdrop are on body
        document.querySelector("body > .quick-add-view")?.remove();
        if (this.backdrop) {
          this.backdrop.remove();
          this.backdrop = null;
        }
        this.isOverlayOpen = false;
        document.removeEventListener("click", this.handleInteractionOutside);
      }

      disconnectedCallback() {
        document.removeEventListener("click", this.handleInteractionOutside);
      }
    },
  );
}
